<template>
    <el-dialog
        :visible.sync="visible"
        title="Customer Profile Registration"
        custom-class="dialog-no-border"
        :close-on-click-modal="false"
        :before-close="handleBeforeClose"
        @close="$emit('close')"
    >
        <div slot="title">
            <h5 class="mb-0">Customer Profile Registration</h5>
        </div>

        <el-form
            :model="form"
            :rules="rules"
            v-loading="registering"
            label-position="top"
            ref="customerProfileRegistrationForm"
        >
            <div class="row">
                <div class="col-md-12">
                    <div class="form-label">
                        <h5 class="mb-0">Business Information</h5>
                    </div>
                    <div class="dropdown-divider"></div>

                    <el-form-item prop="customer_name" label="Legal Name">
                        <span class="page-anchor" id="customer_name"></span>
                        <el-input
                            v-model="form.customer_name"
                            @change="preValidateForm('customerProfileRegistrationForm')"
                        />
                    </el-form-item>

                    <el-form-item prop="business_type" label="Business Type">
                        <span class="page-anchor" id="business_type"></span>
                        <el-select
                            v-model="form.business_type"
                            placeholder="Select Business Type"
                            class="w-full"
                            filterable
                            @change="preValidateForm('customerProfileRegistrationForm')"
                        >
                            <el-option
                                v-for="businessType in businessTypes"
                                v-if="!businessType.disabled"
                                :key="businessType.id + businessType.value"
                                :label="businessType.value"
                                :value="businessType.value"
                            >
                            </el-option>
                        </el-select>
                    </el-form-item>

                    <el-form-item prop="company_status" label="Company Status">
                        <span class="page-anchor" id="company_status"></span>
                        <el-radio-group
                            v-model="form.company_status"
                            size="small"
                        >
                            <el-radio-button :label="1">Private</el-radio-button>
                            <el-radio-button :label="2">Public</el-radio-button>
                        </el-radio-group>
                    </el-form-item>

                    <el-form-item
                        v-show="form.company_status === STATUS_PUBLIC"
                        prop="stock_ticker"
                    >
                        <template slot="label">
                            <span class="red-asterisk">*</span>
                            Stock Ticker
                            <el-tooltip
                                class="item"
                                effect="dark"
                                content="If your company is publicly traded, you must provide the Stock Ticker"
                                placement="top">
                                <i class="fa fa-exclamation-circle text-blue"/>
                            </el-tooltip>
                        </template>
                        <span class="page-anchor" id="stock_ticker"></span>
                        <el-input
                            v-model="form.stock_ticker"
                            @change="preValidateForm('customerProfileRegistrationForm')"
                        />
                    </el-form-item>

                    <el-form-item
                        v-show="form.company_status === STATUS_PUBLIC"
                        prop="stock_exchange"
                    >
                        <template slot="label">
                            <span class="red-asterisk">*</span>
                            Stock Exchange
                            <el-tooltip
                                class="item"
                                effect="dark"
                                content="If your company is publicly traded, you must provide the Stock Exchange"
                                placement="top">
                                <i class="fa fa-exclamation-circle text-blue"/>
                            </el-tooltip>
                        </template>
                        <span class="page-anchor" id="stock_exchange"></span>
                        <el-select
                            v-model="form.stock_exchange"
                            placeholder="Select Stock Exchange"
                            class="w-full"
                            filterable
                            @change="preValidateForm('customerProfileRegistrationForm')"
                        >
                            <el-option
                                v-for="stock_exchange in stockExchanges"
                                :key="stock_exchange.id + stock_exchange.value"
                                :label="stock_exchange.value"
                                :value="stock_exchange.value"
                            >
                            </el-option>
                        </el-select>
                    </el-form-item>

                    <el-form-item
                        v-show="form.company_status === STATUS_PUBLIC"
                        prop="brand_contact_email"
                    >
                        <template slot="label">
                            <span class="red-asterisk">*</span>
                            Brand Contact Email
                            <el-tooltip
                                class="item"
                                effect="dark"
                                content="Please provide an email that your customer have access to so that Auth+ 2FA can be completed"
                                placement="top">
                                <i class="fa fa-exclamation-circle text-blue"/>
                            </el-tooltip>
                        </template>
                        <span class="page-anchor" id="brand_contact_email"></span>
                        <el-input
                            v-model.trim="form.brand_contact_email"
                            @input="preValidateForm('customerProfileRegistrationForm')"
                        />
                    </el-form-item>

                    <el-form-item prop="business_industry" label="Business Industry">
                        <span class="page-anchor" id="business_industry"></span>
                        <el-select
                            v-model="form.business_industry"
                            placeholder="Select Business Industry"
                            class="w-full"
                            filterable
                            @change="preValidateForm('customerProfileRegistrationForm')"
                        >
                            <el-option
                                v-for="businessIndustry in businessIndustries"
                                :key="businessIndustry.value"
                                :label="businessIndustry.name"
                                :value="businessIndustry.value"
                            >
                            </el-option>
                        </el-select>
                    </el-form-item>

                    <el-form-item
                        prop="business_registration_identifier"
                        label="Business ID Type"
                    >
                        <span
                            class="page-anchor"
                            id="business_registration_identifier"
                        ></span>
                        <el-select
                            v-model="form.business_registration_identifier"
                            placeholder="Select Business ID Type"
                            class="w-full"
                            filterable
                            value=""
                            @change="preValidateForm('customerProfileRegistrationForm')"
                        >
                            <el-option
                                v-for="businessRegistrationIdentifier in businessRegistrationIdentifiers"
                                :key="businessRegistrationIdentifier.value"
                                :label="businessRegistrationIdentifier.name"
                                :value="businessRegistrationIdentifier.value"
                            >
                            </el-option>
                        </el-select>
                    </el-form-item>

                    <el-form-item
                        v-if="form.business_registration_identifier === 'Other'"
                        prop="other_identifier"
                        label="Name of Other Registration Type"
                    >
                        <span class="page-anchor" id="other_identifier"></span>
                        <el-input
                            v-model="form.other_identifier"
                            @change="preValidateForm('customerProfileRegistrationForm')"
                        />
                    </el-form-item>

                    <el-form-item
                        prop="business_registration_number"
                        label="Business Registration Number"
                    >
                        <template slot="label">
                            <span>Business Registration Number</span>
                            <el-tooltip
                                v-if="showCanadianBusinessRegistrationNumberTooltip"
                                effect="dark"
                                placement="top"
                            >
                                <div slot="content" v-html="showCanadianBusinessRegistrationNumberContent"></div>
                                <i class="fa fa-exclamation-triangle text-blue"/>
                            </el-tooltip>
                        </template>
                        <el-input
                            v-model="form.business_registration_number"
                            @change="preValidateForm('customerProfileRegistrationForm')"
                        />
                    </el-form-item>


                    <el-form-item
                        prop="business_regions_of_operation"
                        label="Business Regions of Operation"
                    >
                        <span
                            class="page-anchor"
                            id="business_regions_of_operation"
                        ></span>
                        <el-select
                            v-model="form.business_regions_of_operation"
                            placeholder="Select Business Regions of Operation"
                            class="w-full"
                            filterable
                            multiple
                            @change="preValidateForm('customerProfileRegistrationForm')"
                        >
                            <el-option
                                v-for="businessRegionsOfOperation in businessRegionsOfOperations"
                                :key="businessRegionsOfOperation.value"
                                :label="businessRegionsOfOperation.name"
                                :value="businessRegionsOfOperation.value"
                            >
                            </el-option>
                        </el-select>
                    </el-form-item>

                    <el-form-item prop="website_url" label="Website URL">
                        <span class="page-anchor" id="website_url"></span>
                        <el-input
                            v-model="form.website_url"
                            placeholder="https://example.com"
                            @input="preValidateForm('customerProfileRegistrationForm')"
                            @blur="() => { website_url = website_url; preValidateForm('customerProfileRegistrationForm'); }"
                        />
                    </el-form-item>

                    <el-form-item prop="voice_integrity_use_case" label="How will you primarily use your phone numbers for calls?">
                        <span class="page-anchor" id="voice_integrity_use_case"></span>
                        <el-select
                            v-model="form.voice_integrity_use_case"
                            class="w-full"
                            filterable
                            placeholder="Select a use case"
                            @change="preValidateForm('customerProfileRegistrationForm')"
                        >
                            <el-option
                                v-for="useCase in voiceIntegrityUseCases"
                                :key="useCase"
                                :label="useCase"
                                :value="useCase"
                            />
                        </el-select>
                    </el-form-item>

                    <el-form-item prop="business_employee_count" label="Number of Employees">
                        <span class="page-anchor" id="business_employee_count"></span>
                        <el-input-number
                            v-model="form.business_employee_count"
                            class="w-100"
                            :min="1"
                            @change="preValidateForm('customerProfileRegistrationForm')"
                        />
                    </el-form-item>

                    <el-form-item prop="average_business_day_call_volume" label="Average Calls per Day">
                        <span class="page-anchor" id="average_business_day_call_volume"></span>
                        <el-input-number
                            v-model="form.average_business_day_call_volume"
                            class="w-100"
                            :min="1"
                            @change="preValidateForm('customerProfileRegistrationForm')"
                        />
                    </el-form-item>

                    <div class="form-label">
                        <h5 class="mt-4">Address Information</h5>
                    </div>
                    <div class="dropdown-divider"></div>

                    <el-form-item prop="iso_country" label="Legal Country">
                        <span class="page-anchor" id="iso_country"></span>
                        <el-select
                            v-model="form.iso_country"
                            placeholder="Select Country"
                            class="w-full"
                            filterable
                            @change="handleCountryChange"
                        >
                            <el-option
                                v-for="country in countries"
                                :key="country.id"
                                :label="country.name"
                                :value="country.id"
                            >
                            </el-option>
                        </el-select>
                    </el-form-item>

                    <el-form-item prop="region" label="State/Province/Region">
                        <span class="page-anchor" id="region"></span>
                        <el-select
                            v-if="form.iso_country === 'US' || form.iso_country === 'CA'"
                            v-model="form.region"
                            placeholder="Select State/Province/Region"
                            class="w-full"
                            filterable
                            @change="preValidateForm('customerProfileRegistrationForm')"
                        >
                            <el-option
                                v-for="region in states"
                                :key="region.value"
                                :label="region.label"
                                :value="region.value"
                            >
                            </el-option>
                        </el-select>
                        <el-input
                            v-else
                            v-model="form.region"
                            @input="preValidateForm('customerProfileRegistrationForm')"
                        />
                    </el-form-item>

                    <el-form-item prop="city" label="City">
                        <span class="page-anchor" id="city"></span>
                        <el-input
                            v-model="form.city"
                            @input="preValidateForm('customerProfileRegistrationForm')"
                        />
                    </el-form-item>

                    <el-form-item prop="street" label="Street">
                        <span class="page-anchor" id="street"></span>
                        <el-input
                            v-model="form.street"
                            @input="preValidateForm('customerProfileRegistrationForm')"
                        />
                    </el-form-item>

                    <el-form-item prop="street_secondary" label="Street 2">
                        <span class="page-anchor" id="street_secondary"></span>
                        <el-input
                            v-model="form.street_secondary"
                            @input="preValidateForm('customerProfileRegistrationForm')"
                        />
                    </el-form-item>

                    <el-form-item prop="postal_code" label="Postal Code">
                        <span class="page-anchor" id="postal_code"></span>
                        <el-input
                            v-model="form.postal_code"
                            @input="preValidateForm('customerProfileRegistrationForm')"
                        />
                    </el-form-item>

                    <div class="form-label">
                        <h5 class="mt-4 mb-0">Authorized Representative Information</h5>
                    </div>
                    <div class="dropdown-divider"></div>

                    <el-form-item prop="auth_rep_first_name" label="First Name">
                        <span class="page-anchor" id="auth_rep_first_name"></span>
                        <el-input
                            v-model="form.auth_rep_first_name"
                            @input="preValidateForm('customerProfileRegistrationForm')"
                        />
                    </el-form-item>

                    <el-form-item prop="auth_rep_last_name" label="Last Name">
                        <span class="page-anchor" id="auth_rep_last_name"></span>
                        <el-input
                            v-model="form.auth_rep_last_name"
                            @input="preValidateForm('customerProfileRegistrationForm')"
                        />
                    </el-form-item>

                    <el-form-item prop="auth_rep_email" label="Email">
                        <span class="page-anchor" id="auth_rep_email"></span>
                        <el-input
                            v-model.trim="form.auth_rep_email"
                            @input="preValidateForm('customerProfileRegistrationForm')"
                        />
                    </el-form-item>

                    <el-form-item prop="auth_rep_phone_number" label="Work Cell">
                        <span class="page-anchor" id="auth_rep_phone_number"></span>
                        <el-input
                            v-model="form.auth_rep_phone_number"
                            @input="preValidateForm('customerProfileRegistrationForm')"
                        />
                    </el-form-item>

                    <el-form-item prop="auth_rep_business_title" label="Title">
                        <span class="page-anchor" id="auth_rep_business_title"></span>
                        <el-input
                            v-model="form.auth_rep_business_title"
                            @input="preValidateForm('customerProfileRegistrationForm')"
                        />
                    </el-form-item>

                    <el-form-item prop="auth_rep_job_position" label="Job Position">
                        <span class="page-anchor" id="auth_rep_job_position"></span>
                        <el-select
                            v-model="form.auth_rep_job_position"
                            placeholder="Select Job Position"
                            class="w-full"
                            filterable
                            @change="preValidateForm('customerProfileRegistrationForm')"
                        >
                            <el-option
                                v-for="jobPosition in authRepJobPositions"
                                :key="jobPosition.id + jobPosition.value"
                                :label="jobPosition.value"
                                :value="jobPosition.value"
                            >
                            </el-option>
                        </el-select>
                    </el-form-item>

                    <div class="row">
                        <div class="col-md-12">
                            <el-button
                                type="primary"
                                @click="registerCustomerProfile"
                                class="w-full mt-2"
                                :loading="registering"
                                :disabled="!validated"
                            >
                                Register Customer Profile
                            </el-button>
                        </div>
                    </div>
                </div>
            </div>
        </el-form>
    </el-dialog>
</template>

<script>
import {form_validation_mixin} from '../../../mixins'
import {STATUS_PUBLIC} from '../../../constants/company'
import businessTypes from '../../../constants/compliance-business-types'
import businessRegistrationIdentifiers from '../../../constants/compliance-business-registration-identifiers'
import businessIndustries from '../../../constants/compliance-business-industries'
import businessRegionsOfOperations from '../../../constants/compliance-business-regions-of-operation'
import authRepJobPositions from '../../../constants/compliance-auth-rep-job-positions'
import stockExchanges from '../../../constants/compliance-stock-exchanges'
import usStates from '../../../constants/compliance-business-registration-us-states'
import caProvinces from '../../../constants/compliance-business-registration-ca-provinces'
import {VOICE_INTEGRITY_USE_CASES} from '../../../constants/voice-integrity-use-cases'

const getCountries = () => {
    const countries = window.countriesAndTimezones.getAllCountries()

    // first exclude US & CA, and then extract only id, name fields of the countries
    const countriesArr = Object.values(countries)
        .filter(({id}) => !['US', 'CA'].includes(id))
        .map(({id, name}) => {
            return {id, name}
        })

    // add US & CA to the top and return
    return [
        {id: 'US', name: 'United States'},
        {id: 'CA', name: 'Canada'},
        ...countriesArr,
    ]
}

export default {
    name: 'CustomerProfileRegistrationDialog',
    mixins: [form_validation_mixin],
    props: {
        visible: {
            type: Boolean,
            required: true
        },
        company: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            STATUS_PUBLIC,
            registering: false,
            manualCountryChange: false,
            countries: getCountries(),
            usStates,
            caProvinces,
            states: [],
            businessTypes,
            businessRegistrationIdentifiers,
            businessIndustries,
            businessRegionsOfOperations,
            authRepJobPositions,
            stockExchanges,
            voiceIntegrityUseCases: VOICE_INTEGRITY_USE_CASES,
            form: {
                accepted_compliance_one_time_fee: false,

                // business information
                customer_name: '',
                business_type: '',
                company_status: 1,
                stock_ticker: '',
                stock_exchange: '',
                business_registration_identifier: null,
                other_identifier: '',
                business_industry: '',
                business_registration_number: '',
                business_regions_of_operation: [],
                website_url: '',
                voice_integrity_use_case: '',
                business_employee_count: null,
                average_business_day_call_volume: null,

                // address information
                street: '',
                street_secondary: '',
                city: '',
                region: '',
                postal_code: '',
                iso_country: '',

                // authorized representative information
                auth_rep_first_name: '',
                auth_rep_last_name: '',
                auth_rep_email: '',
                brand_contact_email: '',
                auth_rep_phone_number: '',
                auth_rep_business_title: '',
                auth_rep_job_position: '',
            },
            rules: {
                accepted_compliance_one_time_fee: [
                    {
                        validator: (rule, value, callback) => {
                            if (value === true) {
                                callback()
                            } else {
                                callback(new Error("Please agree one-time fee."))
                            }
                        },
                        trigger: "change",
                    },
                ],
                customer_name: [
                    {
                        required: true,
                        message: "Please provide a business name.",
                        trigger: "blur",
                    },
                ],
                business_type: [
                    {
                        required: true,
                        message: "Business type cannot be empty.",
                        trigger: "change",
                    },
                ],
                company_status: [
                    {
                        required: true,
                        type: "enum",
                        enum: [1, 2],
                        message: "Please select a company status.",
                        trigger: "change",
                    },
                ],
                stock_ticker: [],
                stock_exchange: [],
                brand_contact_email: [],
                business_registration_identifier: [
                    {
                        required: true,
                        message: "Business registration identifier cannot be empty.",
                        trigger: "change",
                    },
                ],
                business_industry: [
                    {
                        required: true,
                        message: "Business industry cannot be empty.",
                        trigger: "change",
                    },
                ],
                business_registration_number: [
                    {
                        required: true,
                        message: "Please provide a business registration number.",
                        trigger: "blur",
                    },
                    {
                        validator: this.validateBusinessRegistrationNumber,
                        trigger: "change",
                    }
                ],
                website_url: [
                    {
                        required: true,
                        message: "Please provide a website URL.",
                        trigger: "blur",
                    },
                    {
                        validator: (rule, value, callback) => {
                            if (typeof value !== 'string' || !value.startsWith('https://')) {
                                callback(new Error('Website URL must start with https://'))
                            } else if (value === 'https://') {
                                callback(new Error('Website URL must include a domain after https://'))
                            } else if (!/^https:\/\/\S+\.\S+$/.test(value)) {
                                callback(new Error('Website URL must be a valid URL'))
                            } else {
                                callback()
                            }
                        },
                        trigger: 'blur'
                    }
                ],
                voice_integrity_use_case: [
                    {required: true, message: 'Please select a use case.', trigger: 'change'},
                ],
                business_employee_count: [
                    {required: true, message: 'Please provide the number of employees.', trigger: 'blur'},
                    {type: 'number', min: 1, message: 'Must be greater than 1.', trigger: 'blur'},
                ],
                average_business_day_call_volume: [
                    {required: true, message: 'Please provide the average calls per day.', trigger: 'blur'},
                    {type: 'number', min: 1, message: 'Must be greater than 1.', trigger: 'blur'},
                ],
                business_regions_of_operation: [
                    {
                        required: true,
                        message: "Business Regions of Operation cannot be empty.",
                        trigger: "change",
                    },
                ],
                street: [
                    {
                        required: true,
                        message: "Please provide a street address.",
                        trigger: "blur",
                    },
                ],
                city: [
                    {required: true, message: "Please provide a city", trigger: "blur"},
                ],
                region: [
                    {
                        required: true,
                        message: "Please provide a state/province/region.",
                        trigger: "blur",
                    },
                ],
                postal_code: [
                    {
                        required: true,
                        message: "Please provide a postal code.",
                        trigger: "blur",
                    },
                ],
                iso_country: [
                    {
                        required: true,
                        message: "Country cannot be empty.",
                        trigger: "change",
                    },
                ],
                auth_rep_first_name: [
                    {
                        required: true,
                        message: "Please provide a first name.",
                        trigger: "blur",
                    },
                ],
                auth_rep_last_name: [
                    {
                        required: true,
                        message: "Please provide a last name.",
                        trigger: "blur",
                    },
                ],
                auth_rep_email: [
                    {
                        required: true,
                        message: "Please provide an email address",
                        trigger: "blur",
                    },
                ],
                auth_rep_phone_number: [
                    {
                        required: true,
                        message: "Please provide a phone number.",
                        trigger: "blur",
                    },
                ],
                auth_rep_business_title: [
                    {
                        required: true,
                        message: "Please provide a business title.",
                        trigger: "blur",
                    },
                ],
                auth_rep_job_position: [
                    {
                        required: true,
                        message: "Job position cannot be empty.",
                        trigger: "blur",
                    },
                ],
            },
        }
    },
    computed: {
        guessedBusinessIdentifier() {
            return this.businessRegistrationIdentifiers.find(identifier => 
                identifier.country === this.company.country || identifier.country === this.company.legal_country
            )
        },
        website_url: {
            get() {
                return this.form.website_url;
            },
            set(value) {
                this.form.website_url = this.transformUrl(value);
            }
        },
        showCanadianBusinessRegistrationNumberTooltip() {
            return this.form.business_registration_identifier === 'CBN';
        },
        showCanadianBusinessRegistrationNumberContent() {
            return `Our carrier is ONLY accepting the first 9 numeric digits of the Canadian Business Number (CBN) <br>
              rather than the Corporation/Incorporation/Registry ID numbers for Canadian Brand Registration.
              <br><br><b>Eg. 123456789 instead of &#42;123456789&#42;RT0001.</b>`
        }
    },
    watch: {
        visible(isVisible) {
            if (isVisible) {
                this.populateFormFromCompany();
                this.setInitialRegion();
                this.preValidateForm('customerProfileRegistrationForm');
            }
        },
        'form.iso_country'(new_country) {
            if (this.manualCountryChange) {
                this.form.region = '';
            }
            if (new_country === 'US') {
                this.states = this.usStates;
            } else if (new_country === 'CA') {
                this.states = this.caProvinces;
            } else {
                this.states = [];
            }
            this.setInitialRegion();
        }
    },
    methods: {
        handleBeforeClose(done) {
            this.$confirm(
                'Are you sure you want to close? All entered information will be cleared.',
                'Close confirmation',
                {
                    confirmButtonText: 'Yes, close',
                    cancelButtonText: 'Keep editing',
                    type: 'warning',
                }
            ).then(() => {
                done()
            }).catch(() => {})
        },
        populateFormFromCompany() {
            // Pre-populate the business registration form fields
            this.form.customer_name = this.company.legal_name
            this.form.business_type = this.company.business_type
            this.form.company_status = this.company.company_status
            this.form.stock_ticker = this.company.stock_ticker
            this.form.stock_exchange = this.company.stock_exchange
            this.form.business_registration_identifier = this.company.business_registration_identifier
            
            if (!this.form.business_registration_identifier) {
                // Try to find the identifier from the list by comparing the country code to company.country
                if (this.guessedBusinessIdentifier) {
                    this.form.business_registration_identifier = this.guessedBusinessIdentifier.value
                }
            }
            
            this.form.other_identifier = this.company.other_identifier
            this.form.business_industry = this.company.business_industry
            this.form.business_registration_number = this.company.business_registration_number
            this.form.business_regions_of_operation = this.company.business_regions_of_operation
            this.form.website_url = this.company.website_url
            this.form.voice_integrity_use_case = this.company.voice_integrity_use_case
            this.form.business_employee_count = this.company.business_employee_count
            this.form.average_business_day_call_volume = this.company.average_business_day_call_volume
            this.form.street = this.company.street
            this.form.street_secondary = this.company.street_secondary
            this.form.city = this.company.city
            this.form.region = this.company.region
            this.form.postal_code = this.company.postal_code
            this.form.iso_country = this.company.legal_country
            
            // Pre-populate the point of contact fields
            this.form.auth_rep_first_name = this.company.auth_rep_first_name
            this.form.auth_rep_last_name = this.company.auth_rep_last_name
            this.form.auth_rep_email = this.company.auth_rep_email
            this.form.brand_contact_email = this.company.brand_contact_email
            this.form.auth_rep_phone_number = this.company.auth_rep_phone_number
            this.form.auth_rep_business_title = this.company.auth_rep_business_title
            this.form.auth_rep_job_position = this.company.auth_rep_job_position
        },
        handleCountryChange() {
            this.manualCountryChange = true;
            this.form.region = '';
            this.preValidateForm('customerProfileRegistrationForm');
        },
        setInitialRegion() {
            if (this.form.iso_country === 'US') {
                this.states = this.usStates;
            } else if (this.form.iso_country === 'CA') {
                this.states = this.caProvinces;
            } else {
                this.states = [];
            }
            
            const selected_region = this.states.find(state => state.value === this.form.region);
            
            if (selected_region) {
                this.form.region = selected_region.value;
            }
        },
        validateBusinessRegistrationNumber(rule, value, callback) {
            // We take the value and remove all non-digit characters
            let clean_value = value.replace(/\D+/g, '')
            
            if (this.form.business_registration_identifier === 'CBN') {
                // Business registration number must be 9 digits long
                if (clean_value.length !== 9) {
                    return callback(new Error('The business registration number must be 9 digits long.'));
                }
                this.form.business_registration_number = clean_value
            } else if (this.form.business_registration_identifier === 'EIN') {
                // EIN must be 9 digits long as well
                if (clean_value.length !== 9) {
                    return callback(new Error('EIN must be 9 digits long'))
                }
                // For EIN, we format it as XX-XXXXXXX
                this.form.business_registration_number = `${clean_value.slice(0, 2)}-${clean_value.slice(2)}`
            }
            callback()
        },
        registerCustomerProfile() {
            this.registering = true;
            
            axios
                .post(
                    `/api/admin/compliance/${this.company.id}/customer-profiles`,
                    this.form
                )
                .then(() => {
                    this.$notify({
                        title: "Customer Profile Registration",
                        message: "Operation is being processed...",
                        type: "success",
                        showClose: true,
                    });
                    this.$emit('registered');
                })
                .catch((err) => {
                    this.$root.handleErrors(err.response);
                })
                .finally(() => {
                    this.registering = false;
                });
        }
    }
}
</script>

<style scoped>
.red-asterisk {
    color: rgb(177, 2, 2);
    margin-right: 4px;
}
</style>
